import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { ZenRowsUsageDay } from '../api/types';
import { count, shortDate } from '../utils/format';
import { dollars } from '../utils/usd';

import { ChartTooltip } from './ChartTooltip';
import { Legend } from './Legend';
import { CHROME, SERIES, STATUS } from './palette';

const CREDITS = SERIES[1];
const FAILED = STATUS.critical;

/**
 * Credits per day, with the failed share stacked on top. Cost is not a second
 * series: it is a fixed multiple of credits, so plotting both would draw the
 * same shape twice — it belongs in the tooltip instead.
 */
export function ZenRowsSpendChart({ points }: { points: ZenRowsUsageDay[] }) {
  if (points.length === 0) {
    return (
      <div className="state">
        <div>No paid calls recorded yet.</div>
        <div className="muted">Usage appears here as soon as a request escalates to ZenRows.</div>
      </div>
    );
  }

  const rows = points.map((point) => ({
    ...point,
    label: shortDate(point.day),
    okCredits: point.requests === 0 ? 0 : Math.round((point.credits * (point.requests - point.failures)) / point.requests),
    failedCredits: point.requests === 0 ? 0 : Math.round((point.credits * point.failures) / point.requests),
  }));

  return (
    <>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid stroke={CHROME.grid} vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={{ stroke: CHROME.axis }}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={52}
            allowDecimals={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={({ active, payload }) => {
              if (!active || !payload?.length) {
                return null;
              }

              const row = payload[0].payload as (typeof rows)[number];

              return (
                <ChartTooltip
                  title={row.label}
                  rows={[
                    { key: 'c', label: 'Credits', value: count(row.credits), color: CREDITS },
                    { key: 'u', label: 'Cost', value: dollars(row.costUsd) },
                    { key: 'r', label: 'Calls', value: count(row.requests) },
                    { key: 'f', label: 'Failed calls', value: count(row.failures), color: FAILED },
                    {
                      key: 'a',
                      label: 'Credits per call',
                      value: row.requests === 0 ? '—' : (row.credits / row.requests).toFixed(1),
                    },
                  ]}
                />
              );
            }}
          />
          <Bar isAnimationActive={false} dataKey="okCredits" stackId="c" fill={CREDITS} radius={[0, 0, 0, 0]} maxBarSize={30} />
          <Bar isAnimationActive={false} dataKey="failedCredits" stackId="c" fill={FAILED} radius={[4, 4, 0, 0]} maxBarSize={30} />
        </BarChart>
      </ResponsiveContainer>

      <Legend
        entries={[
          { label: 'Credits on successful calls', color: CREDITS },
          { label: 'Credits burned on failures', color: FAILED },
        ]}
      />
    </>
  );
}
